import React, { useEffect, useMemo, useState } from 'react';
import { settingsApi } from '../services/api';
import { PageHead, Card, Btn, FormGroup, Input, Spinner, Toast, Empty } from '../components/UI';

const HOLIDAY_DRAFT_KEY = 'bookingle_holiday_settings_draft_v1';

function normalizeDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  return raw;
}

function toYmd(dateObj) {
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function prettyDate(value) {
  const [y, m, d] = value.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}

function isSecondSaturdayValue(value) {
  const [y, m, d] = value.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getDay() === 6 && d >= 8 && d <= 14;
}

function isSundayValue(value) {
  const [y, m, d] = value.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getDay() === 0;
}

function isSecondSaturdayDate(dt) {
  return dt.getDay() === 6 && dt.getDate() >= 8 && dt.getDate() <= 14;
}

function monthGrid(monthDate) {
  const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const gridStart = new Date(start);
  gridStart.setDate(start.getDate() - start.getDay());

  const cells = [];
  for (let i = 0; i < 42; i++) {
    const current = new Date(gridStart);
    current.setDate(gridStart.getDate() + i);
    cells.push(current);
  }
  return cells;
}

function toArray(value) {
  if (!Array.isArray(value)) return [];
  return value.map(normalizeDate).filter(Boolean);
}

function normalizeNames(value) {
  if (!value || typeof value !== 'object') return {};
  const next = {};
  Object.entries(value).forEach(([k, v]) => {
    const key = normalizeDate(k);
    const name = String(v || '').trim();
    if (!key || !name) return;
    next[key] = name;
  });
  return next;
}

function extractErrorMessage(err, fallback) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const message =
    data?.message ||
    data?.error ||
    data?.data?.message ||
    data?.data?.error ||
    err?.message;
  return status ? `${fallback} (HTTP ${status})${message ? `: ${message}` : ''}` : (message || fallback);
}
export default function HolidaySettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [sundayClosed, setSundayClosed] = useState(true);
  const [secondSaturdayClosed, setSecondSaturdayClosed] = useState(true);
  const [closedDates, setClosedDates] = useState([]);
  const [openDates, setOpenDates] = useState([]);
  const [holidayNames, setHolidayNames] = useState({});

  const [closedInput, setClosedInput] = useState('');
  const [closedNameInput, setClosedNameInput] = useState('');
  const [openInput, setOpenInput] = useState('');

  const [viewMonth, setViewMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [markMode, setMarkMode] = useState('general');

  useEffect(() => {
    settingsApi.getHolidays()
      .then((r) => {
        const data = r.data?.data || {};
        const serverState = {
          sundayClosed: data.sundayClosed !== false,
          secondSaturdayClosed: data.secondSaturdayClosed !== false,
          closedDates: toArray(data.closedDates),
          openDates: toArray(data.openDates),
          holidayNames: normalizeNames(data.holidayNames),
        };

        let nextState = serverState;
        try {
          const rawDraft = localStorage.getItem(HOLIDAY_DRAFT_KEY);
          if (rawDraft) {
            const parsed = JSON.parse(rawDraft);
            nextState = {
              sundayClosed: parsed?.sundayClosed !== false,
              secondSaturdayClosed: parsed?.secondSaturdayClosed !== false,
              closedDates: toArray(parsed?.closedDates),
              openDates: toArray(parsed?.openDates),
              holidayNames: normalizeNames(parsed?.holidayNames),
            };
            setToast({ type: 'info', msg: 'Unsaved holiday draft restored. Click Save Holidays.' });
          }
        } catch {
          // Ignore malformed local draft.
        }

        setSundayClosed(nextState.sundayClosed);
        setSecondSaturdayClosed(nextState.secondSaturdayClosed);
        setClosedDates(nextState.closedDates);
        setOpenDates(nextState.openDates);
        setHolidayNames(nextState.holidayNames);
      })
      .catch((err) => {
        setToast({ type: 'error', msg: extractErrorMessage(err, 'Unable to load holiday settings.') });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loading) return;
    try {
      localStorage.setItem(HOLIDAY_DRAFT_KEY, JSON.stringify({
        sundayClosed,
        secondSaturdayClosed,
        closedDates,
        openDates,
        holidayNames,
      }));
    } catch {
      // Ignore localStorage write failures.
    }
  }, [loading, sundayClosed, secondSaturdayClosed, closedDates, openDates, holidayNames]);

  const sortedClosedDates = useMemo(
    () => [...new Set(closedDates)].sort((a, b) => a.localeCompare(b)),
    [closedDates]
  );

  const sortedOpenDates = useMemo(
    () => [...new Set(openDates)].sort((a, b) => a.localeCompare(b)),
    [openDates]
  );

  const cleanedHolidayNames = useMemo(() => {
    const validClosed = new Set(sortedClosedDates);
    const next = {};
    Object.entries(holidayNames || {}).forEach(([dateKey, name]) => {
      if (!validClosed.has(dateKey)) return;
      const trimmed = String(name || '').trim();
      if (!trimmed) return;
      next[dateKey] = trimmed;
    });
    return next;
  }, [holidayNames, sortedClosedDates]);

  const closedSet = useMemo(() => new Set(sortedClosedDates), [sortedClosedDates]);
  const openSet = useMemo(() => new Set(sortedOpenDates), [sortedOpenDates]);
  const monthCells = useMemo(() => monthGrid(viewMonth), [viewMonth]);

  function isDefaultClosed(dateObj) {
    if (sundayClosed && dateObj.getDay() === 0) return true;
    if (secondSaturdayClosed && isSecondSaturdayDate(dateObj)) return true;
    return false;
  }

  function dayStatus(dateObj) {
    const ymd = toYmd(dateObj);
    if (openSet.has(ymd)) return 'open_override';
    if (closedSet.has(ymd)) return 'closed_manual';
    if (isDefaultClosed(dateObj)) return 'closed_default';
    return 'open';
  }

  function setHolidayName(dateKey, nameValue) {
    setHolidayNames((prev) => ({ ...prev, [dateKey]: nameValue }));
  }

  function addClosedDate() {
    const value = normalizeDate(closedInput);
    const name = String(closedNameInput || '').trim();
    if (!value) {
      setToast({ type: 'error', msg: 'Select a valid closed date.' });
      return;
    }
    if (!name) {
      setToast({ type: 'error', msg: 'Enter holiday name for General holiday.' });
      return;
    }
    setClosedDates((prev) => [...prev, value]);
    setOpenDates((prev) => prev.filter((d) => d !== value));
    setHolidayNames((prev) => ({ ...prev, [value]: name }));
    setClosedInput('');
    setClosedNameInput('');
  }

  function addOpenDate() {
    const value = normalizeDate(openInput);
    if (!value) {
      setToast({ type: 'error', msg: 'Select a valid open override date.' });
      return;
    }
    setOpenDates((prev) => [...prev, value]);
    setClosedDates((prev) => prev.filter((d) => d !== value));
    setHolidayNames((prev) => {
      const next = { ...prev };
      delete next[value];
      return next;
    });
    setOpenInput('');
  }

  function removeClosedDate(value) {
    setClosedDates((prev) => prev.filter((d) => d !== value));
    setHolidayNames((prev) => {
      const next = { ...prev };
      delete next[value];
      return next;
    });
  }

  function removeOpenDate(value) {
    setOpenDates((prev) => prev.filter((d) => d !== value));
  }

  function markDateFromCalendar(dateObj) {
    const ymd = toYmd(dateObj);

    if (markMode === 'saturday' && dateObj.getDay() !== 6) {
      setToast({ type: 'error', msg: 'Selected day is not Saturday.' });
      return;
    }

    if (markMode === 'sunday' && dateObj.getDay() !== 0) {
      setToast({ type: 'error', msg: 'Selected day is not Sunday.' });
      return;
    }

    if (markMode === 'open') {
      setOpenDates((prev) => [...prev, ymd]);
      setClosedDates((prev) => prev.filter((d) => d !== ymd));
      setHolidayNames((prev) => {
        const next = { ...prev };
        delete next[ymd];
        return next;
      });
      return;
    }

    if (markMode === 'clear') {
      setOpenDates((prev) => prev.filter((d) => d !== ymd));
      setClosedDates((prev) => prev.filter((d) => d !== ymd));
      setHolidayNames((prev) => {
        const next = { ...prev };
        delete next[ymd];
        return next;
      });
      return;
    }

    let holidayName = '';
    if (markMode === 'general') {
      holidayName = window.prompt(`Enter holiday name for ${prettyDate(ymd)}`) || '';
      holidayName = holidayName.trim();
      if (!holidayName) {
        setToast({ type: 'error', msg: 'Holiday name is required for General holiday.' });
        return;
      }
    }

    if (markMode === 'saturday') holidayName = 'Saturday Holiday';
    if (markMode === 'sunday') holidayName = 'Sunday Holiday';

    setClosedDates((prev) => [...prev, ymd]);
    setOpenDates((prev) => prev.filter((d) => d !== ymd));
    setHolidayNames((prev) => ({ ...prev, [ymd]: holidayName }));
  }

  async function save() {
    setSaving(true);
    try {
      await settingsApi.saveHolidays({
        sundayClosed,
        secondSaturdayClosed,
        closedDates: sortedClosedDates,
        openDates: sortedOpenDates,
        holidayNames: cleanedHolidayNames,
      });
      localStorage.removeItem(HOLIDAY_DRAFT_KEY);
      const reload = await settingsApi.getHolidays();
      const saved = reload.data?.data || {};
      setSundayClosed(saved.sundayClosed !== false);
      setSecondSaturdayClosed(saved.secondSaturdayClosed !== false);
      setClosedDates(toArray(saved.closedDates));
      setOpenDates(toArray(saved.openDates));
      setHolidayNames(normalizeNames(saved.holidayNames));
      setToast({ type: 'success', msg: 'Holiday settings saved.' });
    } catch (err) {
      setToast({ type: 'error', msg: extractErrorMessage(err, 'Failed to save holiday settings.') });
    } finally {
      setSaving(false);
    }
  }

  function prevMonth() {
    setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  }

  function nextMonth() {
    setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem', gap: 10 }}>
        <Spinner size={24} />
        <span style={{ color: 'var(--text2)' }}>Loading holiday settings...</span>
      </div>
    );
  }

  return (
    <div className="page-anim">
      <PageHead
        title="Holiday Calendar"
        subtitle="Common holidays for all campuses and schools"
        actions={<Btn variant="primary" onClick={save} disabled={saving}>{saving ? <Spinner size={16} color="white" /> : 'Save Holidays'}</Btn>}
      />

      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: '1rem' }}>
          <FormGroup label="Default Weekly Holidays">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 8 }}>
              <input type="checkbox" checked={sundayClosed} onChange={(e) => setSundayClosed(e.target.checked)} />
              Sunday is holiday
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
              <input type="checkbox" checked={secondSaturdayClosed} onChange={(e) => setSecondSaturdayClosed(e.target.checked)} />
              Second Saturday is holiday
            </label>
          </FormGroup>

          <div style={{ fontSize: 13, color: 'var(--text2)' }}>
            <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--text1)' }}>How this works</div>
            <div>Pick mode, click date in calendar, then Save.</div>
            <div>General holiday asks for holiday name.</div>
            <div>Open override opens any blocked date.</div>
          </div>
        </div>
      </Card>

      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: 'var(--font-head)', fontWeight: 700, fontSize: 18 }}>
            {viewMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn size="sm" variant="ghost" onClick={prevMonth}>Prev</Btn>
            <Btn size="sm" variant="ghost" onClick={nextMonth}>Next</Btn>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <Btn size="sm" variant={markMode === 'general' ? 'primary' : 'ghost'} onClick={() => setMarkMode('general')}>General</Btn>
          <Btn size="sm" variant={markMode === 'saturday' ? 'primary' : 'ghost'} onClick={() => setMarkMode('saturday')}>Saturday</Btn>
          <Btn size="sm" variant={markMode === 'sunday' ? 'primary' : 'ghost'} onClick={() => setMarkMode('sunday')}>Sunday</Btn>
          <Btn size="sm" variant={markMode === 'open' ? 'green' : 'ghost'} onClick={() => setMarkMode('open')}>Open Override</Btn>
          <Btn size="sm" variant={markMode === 'clear' ? 'red' : 'ghost'} onClick={() => setMarkMode('clear')}>Clear</Btn>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))', gap: 6, marginBottom: 8 }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} style={{ textAlign: 'center', fontSize: 12, fontWeight: 700, color: 'var(--text2)', padding: '4px 0' }}>{d}</div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(0,1fr))', gap: 6 }}>
          {monthCells.map((dateObj) => {
            const sameMonth = dateObj.getMonth() === viewMonth.getMonth();
            const ymd = toYmd(dateObj);
            const status = dayStatus(dateObj);

            let bg = 'white';
            let color = 'var(--text1)';
            if (status === 'closed_manual') {
              bg = '#FDEAEA';
              color = '#A72B2B';
            } else if (status === 'closed_default') {
              bg = '#FFF4E8';
              color = '#9D5A12';
            } else if (status === 'open_override') {
              bg = '#E8F7ED';
              color = '#1A7A35';
            }

            return (
              <button
                key={ymd}
                type="button"
                onClick={() => markDateFromCalendar(dateObj)}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  minHeight: 54,
                  background: bg,
                  color,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-body)',
                  opacity: sameMonth ? 1 : 0.45,
                  fontWeight: 700,
                }}
                title={`${prettyDate(ymd)} (${status.replace('_', ' ')})`}
              >
                {dateObj.getDate()}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10, fontSize: 12, color: 'var(--text2)' }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#FDEAEA', marginRight: 6 }} />Closed (manual)</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#FFF4E8', marginRight: 6 }} />Closed (default)</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: '#E8F7ED', marginRight: 6 }} />Open override</span>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: '1rem' }}>
        <Card>
          <div style={{ fontFamily: 'var(--font-head)', fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Extra Closed Dates (General with Name)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 10 }}>
            <Input type="date" value={closedInput} onChange={(e) => setClosedInput(e.target.value)} />
            <Input value={closedNameInput} onChange={(e) => setClosedNameInput(e.target.value)} placeholder="Holiday name" />
            <Btn variant="outline" onClick={addClosedDate}>Add</Btn>
          </div>
          {sortedClosedDates.length === 0 ? <Empty icon="CL" text="No closed dates added" /> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sortedClosedDates.map((d) => (
                <div key={d} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px' }}>
                  <div style={{ fontSize: 13, flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{prettyDate(d)}</div>
                    <div style={{ marginTop: 6 }}>
                      <Input
                        value={holidayNames[d] || ''}
                        onChange={(e) => setHolidayName(d, e.target.value)}
                        placeholder="Holiday name"
                        style={{ maxWidth: 220 }}
                      />
                    </div>
                    <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 4 }}>
                      {isSundayValue(d) ? 'Sunday' : isSecondSaturdayValue(d) ? 'Second Saturday' : 'General holiday'}
                    </div>
                  </div>
                  <Btn size="sm" variant="red" onClick={() => removeClosedDate(d)}>Remove</Btn>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <div style={{ fontFamily: 'var(--font-head)', fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Open Override Dates</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <Input type="date" value={openInput} onChange={(e) => setOpenInput(e.target.value)} />
            <Btn variant="outline" onClick={addOpenDate}>Add</Btn>
          </div>
          {sortedOpenDates.length === 0 ? <Empty icon="OP" text="No open override dates added" /> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sortedOpenDates.map((d) => (
                <div key={d} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px' }}>
                  <div style={{ fontSize: 13 }}>
                    <div style={{ fontWeight: 700 }}>{prettyDate(d)}</div>
                    <div style={{ color: 'var(--text3)', fontSize: 11 }}>Open override</div>
                  </div>
                  <Btn size="sm" variant="red" onClick={() => removeOpenDate(d)}>Remove</Btn>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

